import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';

const ShareableAchievementCard = ({ achievement, user, onClose, onShare }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareData, setShareData] = useState(null);
  const cardRef = useRef(null);

  const getCategoryGradient = (category) => {
    const gradients = {
      milestone: 'from-purple-400 via-purple-500 to-purple-600',
      creative: 'from-blue-400 via-blue-500 to-blue-600',
      engagement: 'from-green-400 via-green-500 to-green-600',
      social: 'from-orange-400 via-orange-500 to-orange-600',
      streak: 'from-red-400 via-red-500 to-red-600',
    };
    return gradients[category] || 'from-gray-400 via-gray-500 to-gray-600';
  };

  const getCategoryEmoji = (category) => {
    const emojis = {
      milestone: '🎯',
      creative: '🎨',
      engagement: '💪',
      social: '🤝',
      streak: '🔥',
    };
    return emojis[category] || '🏆';
  };

  const generateShareableImage = async () => {
    if (!cardRef.current) return;

    setIsGenerating(true);
    try {
      // Use html2canvas to capture the card as an image
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        width: 400,
        height: 500
      });

      // Convert to blob
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        setShareData({
          imageUrl: url,
          text: `🏆 Achievement Unlocked: ${achievement.name}!\n\n${achievement.description}\n\n+${achievement.points} points earned on AdElevate AI Marketing Platform!\n\n#AdElevate #Achievement #Marketing #AI`,
          downloadName: `achievement-${achievement.name.replace(/\s+/g, '-').toLowerCase()}.png`
        });
      }, 'image/png');

    } catch (error) {
      console.error('Error generating shareable image:', error);
      alert('Failed to generate shareable image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShare = async (platform) => {
    if (platform === 'image') {
      await generateShareableImage();
      return;
    }

    if (!shareData && platform !== 'twitter' && platform !== 'linkedin' && platform !== 'copy') {
      await generateShareableImage();
      return;
    }

    const shareText = `🏆 Achievement Unlocked: ${achievement.name}!\n\n${achievement.description}\n\n+${achievement.points} points earned on AdElevate AI Marketing Platform!`;
    const shareUrl = window.location.origin;

    switch (platform) {
      case 'twitter':
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=AdElevate,Achievement,Marketing,AI`;
        window.open(twitterUrl, '_blank');
        break;

      case 'linkedin':
        const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(`Achievement Unlocked: ${achievement.name}`)}&summary=${encodeURIComponent(shareText)}`;
        window.open(linkedinUrl, '_blank');
        break;

      case 'copy':
        try {
          await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
          alert('Achievement details copied to clipboard!');
        } catch (error) {
          console.error('Failed to copy to clipboard:', error);
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = `${shareText}\n\n${shareUrl}`;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Achievement details copied to clipboard!');
        }
        break;

      case 'download':
        if (shareData?.imageUrl) {
          const link = document.createElement('a');
          link.href = shareData.imageUrl;
          link.download = shareData.downloadName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        break;

      default:
        break;
    }

    onShare?.(platform);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Share Achievement
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Shareable Card Preview */}
        <div className="p-6">
          <div
            ref={cardRef}
            className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-6 border-2 border-gray-200"
            style={{ width: '400px', height: '500px', margin: '0 auto' }}
          >
            {/* AdElevate Branding */}
            <div className="text-center mb-4">
              <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
                AdElevate
              </div>
              <div className="text-xs text-gray-600">AI Marketing Platform</div>
            </div>

            {/* Achievement Badge */}
            <div className="flex justify-center mb-4">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl bg-gradient-to-br ${getCategoryGradient(achievement.category)} text-white shadow-xl`}>
                {achievement.badge_icon || getCategoryEmoji(achievement.category)}
              </div>
            </div>

            {/* Achievement Info */}
            <div className="text-center mb-4">
              <div className="text-lg font-bold text-gray-900 mb-1">
                🏆 Achievement Unlocked!
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">
                {achievement.name}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                {achievement.description}
              </p>
              
              {/* Category and Points */}
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  achievement.category === 'milestone' ? 'bg-purple-100 text-purple-800' :
                  achievement.category === 'creative' ? 'bg-blue-100 text-blue-800' :
                  achievement.category === 'engagement' ? 'bg-green-100 text-green-800' :
                  achievement.category === 'social' ? 'bg-orange-100 text-orange-800' :
                  achievement.category === 'streak' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {achievement.category}
                </div>
                <div className="text-lg font-bold text-yellow-600">
                  +{achievement.points} pts
                </div>
              </div>
            </div>

            {/* User Info */}
            <div className="text-center mb-4">
              <div className="text-sm text-gray-700 font-medium">
                Earned by {user?.name || 'AdElevate User'}
              </div>
              <div className="text-xs text-gray-500">
                {formatDate(achievement.unlocked_at)}
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="flex justify-center space-x-2">
              <span className="text-yellow-400">⭐</span>
              <span className="text-orange-400">🎉</span>
              <span className="text-purple-400">✨</span>
            </div>

            {/* Footer */}
            <div className="text-center mt-4">
              <div className="text-xs text-gray-500">
                Join AdElevate and unlock your achievements!
              </div>
            </div>
          </div>

          {/* Share Options */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              Share Your Achievement
            </h4>

            {/* Social Media Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleShare('twitter')}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <span>🐦</span>
                <span>Twitter</span>
              </button>

              <button
                onClick={() => handleShare('linkedin')}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
              >
                <span>💼</span>
                <span>LinkedIn</span>
              </button>

              <button
                onClick={() => handleShare('copy')}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <span>📋</span>
                <span>Copy Text</span>
              </button>

              <button
                onClick={() => handleShare('image')}
                disabled={isGenerating}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                <span>📸</span>
                <span>{isGenerating ? 'Generating...' : 'Save Image'}</span>
              </button>
            </div>

            {/* Download Button */}
            {shareData?.imageUrl && (
              <button
                onClick={() => handleShare('download')}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <span>⬇️</span>
                <span>Download Image</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareableAchievementCard;